import { BaseComponent } from './BaseComponent.js';
import { DOMHelpers } from '../utils/DOMHelpers.js';

/**
 * Modal
 * Themed modal component (fixes white-on-white bug)
 * Replaces native prompt() dialogs with CSS-variable-based modals
 */
export class Modal extends BaseComponent {
    constructor(options = {}) {
        super();
        this.options = {
            title: options.title || 'Modal',
            content: options.content || '',
            showClose: options.showClose !== false,
            onClose: options.onClose || (() => {}),
            ...options
        };
    }

    render() {
        const overlay = DOMHelpers.createElement('div', {
            className: 'modal-overlay',
            onClick: (e) => {
                if (e.target === overlay) {
                    this.close();
                }
            }
        });

        const modal = DOMHelpers.createElement('div', {
            className: 'modal'
        });

        // Header
        const header = DOMHelpers.createElement('div', {
            className: 'modal-header'
        }, [
            DOMHelpers.createElement('h3', {
                className: 'modal-title'
            }, [this.options.title])
        ]);

        if (this.options.showClose) {
            const closeBtn = DOMHelpers.createElement('button', {
                className: 'modal-close',
                onClick: () => this.close()
            }, ['×']);
            header.appendChild(closeBtn);
        }

        // Body
        const body = DOMHelpers.createElement('div', {
            className: 'modal-body'
        });

        if (typeof this.options.content === 'string') {
            body.innerHTML = this.options.content;
        } else if (this.options.content instanceof HTMLElement) {
            body.appendChild(this.options.content);
        }

        // Footer (optional)
        let footer;
        if (this.options.footer) {
            footer = DOMHelpers.createElement('div', {
                className: 'modal-footer'
            });
            if (typeof this.options.footer === 'string') {
                footer.innerHTML = this.options.footer;
            } else if (Array.isArray(this.options.footer)) {
                this.options.footer.forEach(btn => footer.appendChild(btn));
            }
        }

        modal.appendChild(header);
        modal.appendChild(body);
        if (footer) modal.appendChild(footer);

        overlay.appendChild(modal);
        this.element = overlay;

        return overlay;
    }

    close() {
        this.options.onClose();
        this.destroy();
    }

    /**
     * Static factory: Input modal
     * REPLACES: prompt()
     */
    static inputModal(title, label, placeholder = '', onSubmit) {
        return new Promise((resolve) => {
            let resolved = false;

            const input = DOMHelpers.createElement('input', {
                type: 'text',
                className: 'modal-input',
                placeholder
            });

            const errorDiv = DOMHelpers.createElement('div', {
                className: 'modal-error',
                style: 'display: none; color: var(--color-error-600); margin-top: 0.5rem;'
            });

            const content = DOMHelpers.createElement('div', {}, [
                DOMHelpers.createElement('label', {
                    className: 'modal-label'
                }, [label]),
                input,
                errorDiv
            ]);

            const showError = (message) => {
                errorDiv.textContent = message;
                errorDiv.style.display = 'block';
            };

            const submitBtn = DOMHelpers.createElement('button', {
                className: 'btn btn-primary',
                onClick: async () => {
                    const value = input.value.trim();
                    if (!value) {
                        showError('This field is required');
                        return;
                    }

                    try {
                        let result = value;
                        if (onSubmit) {
                            result = await onSubmit(value);
                        }
                        resolved = true;
                        modal.close();
                        resolve(result);
                    } catch (error) {
                        showError(error.message);
                    }
                }
            }, ['Submit']);

            const cancelBtn = DOMHelpers.createElement('button', {
                className: 'btn btn-secondary',
                onClick: () => {
                    resolved = true;
                    modal.close();
                    resolve(null);
                }
            }, ['Cancel']);

            const modal = new Modal({
                title,
                content,
                footer: [submitBtn, cancelBtn],
                onClose: () => {
                    if (!resolved) {
                        resolve(null);
                    }
                }
            });

            modal.mount(document.body);

            // Focus input
            setTimeout(() => input.focus(), 100);

            // Submit on Enter
            input.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    submitBtn.click();
                }
            });
        });
    }

    /**
     * Static factory: Confirm modal
     */
    static confirmModal(title, message, onConfirm) {
        return new Promise((resolve) => {
            const content = DOMHelpers.createElement('p', {
                className: 'modal-message'
            }, [message]);

            const confirmBtn = DOMHelpers.createElement('button', {
                className: 'btn btn-primary',
                onClick: async () => {
                    try {
                        if (onConfirm) {
                            await onConfirm();
                        }
                        resolve(true);
                        modal.close();
                    } catch (error) {
                        console.error('Confirm error:', error);
                        resolve(false);
                        modal.close();
                    }
                }
            }, ['Confirm']);

            const cancelBtn = DOMHelpers.createElement('button', {
                className: 'btn btn-secondary',
                onClick: () => {
                    resolve(false);
                    modal.close();
                }
            }, ['Cancel']);

            const modal = new Modal({
                title,
                content,
                footer: [confirmBtn, cancelBtn],
                onClose: () => resolve(false)
            });

            modal.mount(document.body);
        });
    }

    /**
     * Static factory: Alert modal
     */
    static alertModal(title, message) {
        return new Promise((resolve) => {
            const content = DOMHelpers.createElement('p', {
                className: 'modal-message'
            }, [message]);

            const okBtn = DOMHelpers.createElement('button', {
                className: 'btn btn-primary',
                onClick: () => {
                    modal.close();
                    resolve();
                }
            }, ['OK']);

            const modal = new Modal({
                title,
                content,
                footer: [okBtn],
                onClose: () => resolve()
            });

            modal.mount(document.body);
        });
    }

    /**
     * Static factory: Custom product modal
     * REPLACES: startCustomProduct() prompt (line 904)
     */
    static customProductModal(onSubmit) {
        return Modal.inputModal(
            '✨ Custom Product',
            'Enter your product or service name:',
            'e.g., AccuMatch BI, Business Analytics Platform',
            onSubmit
        );
    }

    /**
     * Static factory: URL scraper modal
     * REPLACES: scrapeURL() prompt (line 954)
     */
    static urlScraperModal(onSubmit) {
        return Modal.inputModal(
            '🔗 Analyze Website',
            'Enter the URL to analyze:',
            'https://example.com',
            onSubmit
        );
    }

    /**
     * Static factory: Toast notification
     * Shows a brief notification that auto-dismisses for success/info
     * Error toasts persist until user dismisses
     * @param {string} message - Message to display
     * @param {string} type - 'success' | 'error' | 'info' | 'warning'
     * @param {number} duration - Auto-dismiss duration in ms (ignored for error type)
     */
    static toast(message, type = 'success', duration = 4000) {
        // Get or create toast container
        let container = document.querySelector('.toast-container');
        if (!container) {
            container = document.createElement('div');
            container.className = 'toast-container';
            document.body.appendChild(container);
        }

        // Create toast element
        const toast = document.createElement('div');
        toast.className = `toast ${type}`;

        const messageSpan = document.createElement('span');
        messageSpan.className = 'toast-message';
        messageSpan.textContent = message;
        toast.appendChild(messageSpan);

        // Error toasts get a close button and persist
        // Other types auto-dismiss but can also be closed
        const closeBtn = document.createElement('button');
        closeBtn.className = 'toast-close';
        closeBtn.innerHTML = '&times;';
        closeBtn.setAttribute('aria-label', 'Dismiss');
        closeBtn.addEventListener('click', () => dismissToast(toast));
        toast.appendChild(closeBtn);

        container.appendChild(toast);

        // Trigger show animation
        requestAnimationFrame(() => {
            toast.classList.add('show');
        });

        // Auto-dismiss for non-error types
        let timeoutId = null;
        if (type !== 'error') {
            timeoutId = setTimeout(() => dismissToast(toast), duration);
        }

        function dismissToast(toastEl) {
            if (timeoutId) clearTimeout(timeoutId);
            toastEl.classList.remove('show');
            toastEl.classList.add('hiding');
            setTimeout(() => {
                toastEl.remove();
                // Remove container if empty
                if (container && container.children.length === 0) {
                    container.remove();
                }
            }, 300);
        }
    }
}
